import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy as CopyIcon, Check as CheckIcon } from '@mui/icons-material';
import { useCreateLogShare } from '@/hooks/use-log-share';
import {
  logShareUrl,
  MAX_LOG_SHARE_MARKED,
  type LogShareFile,
  type LogShareLine,
} from '@/lib/log-share.service';
import { colors, typography as t, radius } from '@/theme/tokens';
import { FeatureBadge, BETA_FEATURE_HINT } from '@/components/ui/demo-feature';

interface LogShareDialogProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  /** Marked lines plus the context carried around them, already translated. */
  lines: LogShareLine[];
  /** Viewer refs so the recipient can reopen the full session. */
  files: LogShareFile[];
}

/**
 * Turns a set of marked lines into a link.
 *
 * The note field is first and focused because it is the point: a bare link says
 * "look at this", a link with one sentence says what to look for, and that is
 * the difference between an escalation someone can act on and one they have to
 * reply to.
 */
export function LogShareDialog({ open, onClose, deviceId, lines, files }: LogShareDialogProps) {
  const [note, setNote] = useState('');
  const [code, setCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const create = useCreateLogShare();

  const markedCount = lines.filter((l) => l.marked).length;
  const contextCount = lines.length - markedCount;
  const tooMany = markedCount > MAX_LOG_SHARE_MARKED;

  // Reset on every open so a second share does not inherit the first one's note
  // or, worse, show its link.
  useEffect(() => {
    if (open) {
      setNote('');
      setCode(null);
      setCopied(false);
      create.reset();
    }
    // create.reset is stable enough in practice; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCreate = async () => {
    const res = await create.mutateAsync({
      device_id: deviceId,
      note: note.trim(),
      lines,
      files,
    });
    setCode(res.code);
    // Copy immediately — the reason anyone opened this dialog is to paste a link
    // somewhere, so making them click twice is pure friction.
    try {
      await navigator.clipboard.writeText(logShareUrl(res.code));
      setCopied(true);
    } catch {
      // Clipboard can be blocked by permissions or an insecure origin. The URL
      // is on screen and selectable, so this is not worth an error.
    }
  };

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(logShareUrl(code));
      setCopied(true);
    } catch {
      /* see above */
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ fontSize: 17, fontWeight: t.fontWeight.semibold, display: 'flex',
              alignItems: 'center', gap: 1.25 }}
      >
        Share {markedCount} marked line{markedCount === 1 ? '' : 's'}
        {/* This is the moment someone commits to sending a link, so it is the
            right place to say what "beta" costs them — which is nothing. */}
        <Tooltip title={BETA_FEATURE_HINT} placement="right">
          <Box component="span" sx={{ display: 'inline-flex', cursor: 'help' }}>
            <FeatureBadge label="Beta" tone="beta" />
          </Box>
        </Tooltip>
      </DialogTitle>
      {/* pt is deliberate. MUI zeroes a DialogContent's top padding when it
          follows a DialogTitle, and DialogContent scrolls with overflow-y:auto —
          so an outlined TextField's floating label, which renders ABOVE the
          input's top border, gets clipped by the scroll box. 20px clears it. */}
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '20px !important' }}>
        {tooMany && (
          <Alert severity="warning">
            {markedCount} lines is past the {MAX_LOG_SHARE_MARKED}-line limit for an
            excerpt. Share the file itself instead.
          </Alert>
        )}

        {!code ? (
          <>
            <TextField
              autoFocus
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              label="What should they look at?"
              placeholder="Optional, but it saves a round trip."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={create.isPending}
            />
            <Box
              sx={{
                bgcolor: colors.background.page,
                borderRadius: `${radius.md}px`,
                p: 1.5,
                fontFamily: 'monospace',
                fontSize: 11.5,
                lineHeight: 1.8,
                color: colors.text.secondary,
              }}
            >
              <strong>Included:</strong> {markedCount} marked
              {contextCount > 0 && ` · ${contextCount} context`} ·{' '}
              {files.length} file{files.length === 1 ? '' : 's'}
              <br />
              <strong>Opens for:</strong> anyone who can already read this device's logs
            </Box>
            {create.isError && (
              <Alert severity="error">
                Could not create the link. {(create.error as Error)?.message ?? ''}
              </Alert>
            )}
          </>
        ) : (
          <>
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: t.fontWeight.semibold,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: colors.text.tertiary,
                  mb: 0.75,
                }}
              >
                Link
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: `${radius.md}px`,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    px: 1.5,
                    py: 1,
                    fontFamily: 'monospace',
                    fontSize: 11.5,
                    color: colors.text.secondary,
                    bgcolor: colors.background.page,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {logShareUrl(code)}
                </Box>
                <Button
                  onClick={handleCopy}
                  startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                  sx={{ borderRadius: 0, textTransform: 'none', px: 2 }}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </Box>
            </Box>
            <Alert severity="success" sx={{ py: 0.5 }}>
              Saved. The lines travel with the link, so it keeps working after these log
              files rotate out of the archive.
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {code ? 'Done' : 'Cancel'}
        </Button>
        {!code && (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={create.isPending || markedCount === 0 || tooMany}
            startIcon={create.isPending ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ textTransform: 'none' }}
          >
            {create.isPending ? 'Creating…' : 'Create link'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default LogShareDialog;
